"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.connection = exports.ConnectionManager = void 0;
const chain_id_1 = require("@dcl/schemas/dist/dapps/chain-id");
const provider_type_1 = require("@dcl/schemas/dist/dapps/provider-type");
const types_1 = require("@web3-react/types");
const connectors_1 = require("./connectors");
const storage_1 = require("./storage");
const configuration_1 = require("./configuration");
const ProviderAdapter_1 = require("./ProviderAdapter");
require("./declarations");
class ConnectionManager {
    constructor(storage) {
        this.storage = storage;
        /**
         * Obtain the name of the underlying wallet providing the connection.
         * Will only return a value if the provider is a WalletConnectV2Connector that has an ongoing session.
         * TODO: Enhance it to return the name of the wallet for other providers as well if possible.
         */
        this.getWalletName = () => {
            if (this.connector instanceof connectors_1.WalletConnectV2Connector) {
                return this.connector.getWalletName();
            }
            return undefined;
        };
        this.clearConnectionData = () => {
            const { storageKey } = configuration_1.getConfiguration();
            this.storage.remove(storageKey);
            // Clear any data that might have been stored by the different connectors.
            // Clearing them even if they were not the ones used is not an issue as it is a cheap operation.
            connectors_1.WalletConnectV2Connector.clearStorage(this.storage);
        };
        this.handleWeb3ReactDeactivate = () => {
            if (this.connector) {
                this.connector.removeListener(types_1.ConnectorEvent.Deactivate, this.handleWeb3ReactDeactivate);
                this.connector.removeAllListeners(types_1.ConnectorEvent.Update);
            }
            // Whenever the user manually disconnects the account from their wallet, the event will be
            // intercepted by this handler, calling the disconnect method.
            // Necessary to sanitize the state and prevent the continuation of a dead connection.
            this.disconnect().catch(console.error);
        };
    }
    async connect(providerType, chainId = chain_id_1.ChainId.ETHEREUM_MAINNET) {
        this.connector = this.buildConnector(providerType, chainId);
        this.connector.on(types_1.ConnectorEvent.Deactivate, this.handleWeb3ReactDeactivate);
        if (providerType === provider_type_1.ProviderType.MAGIC) {
            this.connector.on(types_1.ConnectorEvent.Update, ({ chainId }) => {
                if (chainId) {
                    this.setConnectionData(providerType, chainId);
                }
            });
        }
        const { provider, account } = await this.connector.activate();
        this.setConnectionData(providerType, chainId);
        return {
            provider: ProviderAdapter_1.ProviderAdapter.adapt(provider),
            providerType,
            account: account || '',
            chainId
        };
    }
    async tryPreviousConnection() {
        const connectionData = this.getConnectionData();
        if (!connectionData) {
            throw new Error('Could not find a valid provider. Make sure to call the `connect` method first');
        }
        const response = await this.connect(connectionData.providerType, connectionData.chainId);
        // If the provider type is injected, the chainId could have changed since previous connection and still connect successfully.
        // We need to check if the chainId has changed, and update the connectionData if so.
        if (response.providerType === provider_type_1.ProviderType.INJECTED) {
            const currentChainIdHex = (await response.provider.request({
                method: 'eth_chainId'
            }));
            const currentChainId = currentChainIdHex
                ? parseInt(currentChainIdHex, 16)
                : null;
            if (currentChainId && connectionData.chainId !== currentChainId) {
                this.setConnectionData(connectionData.providerType, currentChainId);
            }
        }
        return Object.assign(Object.assign({}, response), { chainId: this.getConnectionData().chainId });
    }
    getAvailableProviders() {
        const available = [
            provider_type_1.ProviderType.FORTMATIC,
            provider_type_1.ProviderType.WALLET_CONNECT,
            provider_type_1.ProviderType.WALLET_LINK
        ];
        if (typeof window !== 'undefined' && window.ethereum !== undefined) {
            available.unshift(provider_type_1.ProviderType.INJECTED);
        }
        else {
            // we're assuming if there's no window, it's mobile
            available.unshift(provider_type_1.ProviderType.METAMASK_MOBILE);
        }
        return available;
    }
    async disconnect() {
        if (this.connector) {
            this.connector.deactivate();
            if (this.isClosableConnector()) {
                await this.connector.close();
            }
        }
        this.clearConnectionData();
        this.connector = undefined;
    }
    async getProvider() {
        if (!this.connector) {
            throw new Error('No valid connector found. Please .connect() first');
        }
        return this.connector.getProvider();
    }
    async createProvider(providerType, chainId = chain_id_1.ChainId.ETHEREUM_MAINNET) {
        const connector = this.buildConnector(providerType, chainId);
        const provider = await connector.getProvider();
        return ProviderAdapter_1.ProviderAdapter.adapt(provider);
    }
    buildConnector(providerType, chainId) {
        switch (providerType) {
            case provider_type_1.ProviderType.INJECTED:
                return new connectors_1.InjectedConnector(chainId);
            case provider_type_1.ProviderType.FORTMATIC:
                return new connectors_1.FortmaticConnector(chainId);
            case provider_type_1.ProviderType.MAGIC:
                return new connectors_1.MagicConnector(chainId);
            case provider_type_1.ProviderType.WALLET_LINK:
                return new connectors_1.WalletLinkConnector(chainId);
            case provider_type_1.ProviderType.NETWORK:
                return new connectors_1.NetworkConnector(chainId);
            case provider_type_1.ProviderType.WALLET_CONNECT_V2:
                return new connectors_1.WalletConnectV2Connector(chainId);
            default:
                throw new Error(`Invalid provider ${providerType}`);
        }
    }
    getConnectionData() {
        const { storageKey } = configuration_1.getConfiguration();
        const connectionData = this.storage.get(storageKey);
        return connectionData ? JSON.parse(connectionData) : undefined;
    }
    setConnectionData(providerType, chainId) {
        const { storageKey } = configuration_1.getConfiguration();
        const connectionData = JSON.stringify({
            providerType,
            chainId
        });
        this.storage.set(storageKey, connectionData);
    }
    isClosableConnector() {
        return this.connector && typeof this.connector['close'] !== 'undefined';
    }
}
exports.ConnectionManager = ConnectionManager;
exports.connection = new ConnectionManager(new storage_1.LocalStorage());
//# sourceMappingURL=ConnectionManager.js.map